<template>
  <v-card class="booking-group-booking-card mb-3 pa-2 full-width">
    <div class="d-flex pl-3 pb-3">
      <div class="my-auto text-subtitle-1 d-flex">
        <div>Bokning #{{ index + 1 }}</div>
        <div v-show="isActiveBooking" class="active-booking-indicator ml-3 my-auto"></div>
      </div>
      <div class="my-auto ml-auto">
        <v-btn
          v-if="!booking.id"
          icon
          @click.stop="deleteBooking"
        >
          <v-icon color="error">
            mdi-trash-can
          </v-icon>
        </v-btn>
      </div>
    </div>

    <div>
      <v-row class="ma-0">
        <v-col
          cols="12"
          lg="6"
          class="pt-0"
        >
          <position-picker
            outlined
            exclude-unspecified-positions
            :show-map-icon="showMapButton"
            :map-is-open="isShowMap"
            :selected-position="booking?.position"
            :check-in="booking?.checkIn"
            :check-out="booking?.checkOut"
            :occupancy="occupancy"
            :dogs="booking?.dogs"
            :existing-booking-id="booking?.id"
            :booking-client-id="booking?.clientId"
            @map-icon-clicked="toggleMap"
            @set-position="setPosition"
            @set-validation="setPositionValidation"
          ></position-picker>
        </v-col>
        <v-col
          v-if="hasMultipleLodgingTypes"
          cols="12"
          lg="3"
          class="pt-0"
        >
          <lodging-type-picker
            v-model="lodgingType"
            :lodging-types="booking.category.lodgingTypes"
          ></lodging-type-picker>
        </v-col>
        <v-col
          cols="12"
          :lg="guestPickerColumnWidth"
          class="pt-0"
        >
          <guest-picker
            :booking-client-id="booking?.clientId"
            :max-guests="guestLimits.maxGuests"
            :max-adults="guestLimits.maxAdults"
            :max-children="guestLimits.maxChildren"
            :adults="booking?.adults"
            :children="booking?.children"
            :dogs="booking?.dogs"
            @set-adults="setAdults"
            @set-children="setChildren"
            @set-dogs="setDogs"
          ></guest-picker>
        </v-col>
      </v-row>

      <v-row class="ma-0">
        <v-col
          v-if="showDatePicker"
          cols="12"
          lg="6"
          class="pt-0"
        >
          <date-range-picker
            title="In- och utcheckningsdatum"
            prompt-title-first-date="Välj incheckningsdatum"
            prompt-title-second-date="Välj utcheckningsdatum"
            :first-date="booking?.checkIn"
            :second-date="booking?.checkOut"
            :is-mobile="isMobile"
            :validation="{ hasError: false, errorText: '' }"
            allow-all-dates
            @set-first-date="setCheckInDate"
            @set-second-date="setCheckOutDate"
          ></date-range-picker>
        </v-col>
        <v-col
          cols="12"
          lg="6"
          class="pt-0"
        >
          <v-btn
            v-if="!showCustomerSelector"
            @click="showCustomerSelector = true"
            text
            color="secondary"
            large
            style="margin-top: 15px; margin-bottom: 15px;"
          >
            Lägg till annan gäst
          </v-btn>
          <customer-selector
            v-else
            v-model="customer"
            outlined
            :is-mobile="isMobile"
            cke-enabled
          ></customer-selector>
        </v-col>
        <v-col
          v-if="showBookingFeeOptions"
          cols="12"
          lg="6"
          class="pt-0 d-flex"
          style="height: 86px;"
        >
          <booking-fee-options
            v-model="appliedFees"
            :booking="booking"
            :is-regular-booking="isRegularBooking"
            is-part-of-booking-group
            hide-label
            class="my-auto"
          ></booking-fee-options>
        </v-col>
        <v-col
          v-if="showCancellationPolicyPicker"
          cols="12"
          lg="6"
          class="pt-0"
        >
          <cancellation-policy-picker
            :policies="availableCancellationPolicies"
            :selected-policy="booking?.cancellationPolicy"
            :check-in-date="booking?.checkIn"
            :booking-id="booking?.id"
            :disabled="!!booking?.id"
            :is-booking-group="false"
            :chips="false"
            @set-policy="setCancellationPolicy"
          ></cancellation-policy-picker>
        </v-col>
      </v-row>
    </div>
    <booking-fields
      :booking="booking"
      optional
    ></booking-fields>
  </v-card>
</template>

<script>
import BookingFields from '@/components/booking/form/BookingFields.vue'
import { mapActions } from 'vuex'

import PositionPicker from '@/components/position/PositionPicker.vue'
import LodgingTypePicker from '@/components/position/LodgingTypePicker.vue'
import GuestPicker from '@/components/booking/form/GuestPicker.vue'
import CancellationPolicyPicker from '@/components/cancellation-policy/CancellationPolicyPicker.vue'
import BookingFeeOptions from '@/components/booking/form/FeeOptions.vue'
import CustomerSelector from '@/components/customer/Selector.vue'
import DateRangePicker from '@/components/common/DateRangePicker.vue'

export default {
  name: 'BookingGroupBookingCard',
  components: {
    BookingFields,
    PositionPicker,
    LodgingTypePicker,
    GuestPicker,
    CancellationPolicyPicker,
    BookingFeeOptions,
    CustomerSelector,
    DateRangePicker
  },
  props: {
    booking: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    activeBookingGroupBookingIndex: {
      type: Number,
      required: true
    },
    isShowMap: {
      type: Boolean,
      required: true
    },
    isRegularBooking: Boolean,
    showMapButton: Boolean,
    showDatePicker: Boolean,
    showCancellationPolicyPicker: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    showCustomerSelector: false
  }),
  computed: {
    isActiveBooking: function () {
      return this.activeBookingGroupBookingIndex === this.index
    },
    occupancy: function () {
      return this.booking ? this.booking.adults + this.booking.children : null
    },
    showBookingFeeOptions: function () {
      return !!this.booking
    },
    guestLimits: function () {
      return {
        maxGuests: this.booking?.category?.max_guests ?? 99,
        maxAdults: this.booking?.category?.max_adults ?? 99,
        maxChildren: this.booking?.category?.max_children ?? 99
      }
    },
    hasMultipleLodgingTypes: function () {
      return (this.booking?.category?.lodgingTypes?.length ?? 0) > 1
    },
    guestPickerColumnWidth: function () {
      return this.hasMultipleLodgingTypes ? 3 : 6
    },
    availableCancellationPolicies: function () {
      if (!this.booking?.category) {
        return []
      }

      const availableOn = this.booking.type === 'season'
        ? 'reception_season'
        : 'reception_standard'

      return this.booking.category.cancellationPolicies.filter(policy => policy.available_on.includes(availableOn))
    },
    lodgingType: {
      get: function () {
        return this.booking?.lodgingType
      },
      set: function (value) {
        this.updateBookingField('lodgingType', value)
      }
    },
    customer: {
      get: function () {
        return this.booking?.customer
      },
      set: function (value) {
        this.updateBookingField('customer', value)
      }
    },
    appliedFees: {
      get: function () {
        return this.booking?.appliedFees
      },
      set: function (value) {
        this.updateBookingField('appliedFees', value)
      }
    },
    isMobile: function () {
      return this.$vuetify.breakpoint.mdAndDown
    }
  },
  methods: {
    ...mapActions('bookingForm', [
      'removeBookingDraftFromBookingGroup',
      'setBookingGroupBookingField',
      'setBookingGroupPosition'
    ]),
    deleteBooking: function () {
      if (this.booking.id) {
        return false
      }

      return this.removeBookingDraftFromBookingGroup(this.index)
    },
    toggleMap: function () {
      this.$emit('toggle-map')
    },
    setPosition: function (position) {
      return this.setBookingGroupPosition(position)
    },
    setPositionValidation: function (validation) {
      this.$emit('set-position-validation', validation)
    },
    setAdults: function (adults) {
      this.updateBookingField('adults', adults)
    },
    setChildren: function (children) {
      this.updateBookingField('children', children)
    },
    setDogs: function (dogs) {
      this.updateBookingField('dogs', dogs)
    },
    setCheckInDate: function (checkIn) {
      this.updateBookingField('checkIn', checkIn)
    },
    setCheckOutDate: function (checkOut) {
      this.updateBookingField('checkOut', checkOut)
    },
    setCancellationPolicy: function (policy) {
      this.updateBookingField('cancellationPolicy', policy)
    },
    updateBookingField: function (field, value) {
      return this.setBookingGroupBookingField({
        bookingClientId: this.booking.clientId,
        field,
        value
      })
    }
  }
}
</script>
